import { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { colorsLight } from "../../themes";
import { InformativeModal } from "../Modal";
import { SecondaryButton } from "../SecondaryButton";
import { CohereTable } from "../CohereTable";
import type { Column } from "../CohereTable";
import {
  getReadingLevelLabel,
  getReadingLevelRanges,
  ReadingLevelRange,
} from "../../util/flesch-kincaid/fleschKincaidUtils";

interface ReadingLevelsModalProps {
  open: boolean;
  onClose: () => void;
  easeScore?: number;
}

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 10,
    "& [aria-label='Dismiss modal']": {
      top: 5,
      right: 2,
      color: colorsLight.font.main,
    },
    "& [aria-label='Dismiss modal'] svg": {
      fontSize: 32,
    },
  },
  title: {
    fontFamily: "Figtree",
    fontWeight: 600,
    fontSize: 24,
    lineHeight: 1.2,
    color: "#384358",
  },
  subtitle: {
    display: "block",
    padding: 0,
    fontFamily: "Figtree",
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 1.2,
    color: "rgba(0, 0, 0, 0.6)",
  },
  table: {
    width: "100%",
    "& [data-testid^='table-row-'] > div > div": {
      padding: "12px 0",
    },
    "& [data-testid^='table-row-']:last-child > div > div": {
      paddingBottom: 0,
    },
  },
  headerRow: {
    display: "flex",
    paddingTop: 4,
    paddingBottom: 7,
    fontFamily: "Figtree",
    fontSize: 13,
    lineHeight: 1.2,
    color: colorsLight.font.light,
  },
  cell: {
    display: "block",
    fontFamily: "Figtree",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "16px",
    color: colorsLight.font.main,
  },
  scoreCell: {
    paddingLeft: 28,
  },
  currentCell: {
    fontWeight: 600,
  },
  closeButton: {
    marginTop: 24,
    minWidth: 120,
    height: 48,
    padding: "12px 24px",
    borderRadius: 4,
    fontFamily: "Figtree",
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1.2,
  },
  content: {
    width: 612,
    padding: "27px 36px 40px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0,
  },
}));

export function ReadingLevelsModal({ open, onClose, easeScore }: ReadingLevelsModalProps) {
  const classes = useStyles();
  const ranges = useMemo(() => getReadingLevelRanges(), []);
  const currentLabel = easeScore === undefined ? undefined : getReadingLevelLabel(easeScore);
  const tableColumns: Column<ReadingLevelRange>[] = useMemo(() => {
    const cellClassName = (row: ReadingLevelRange) =>
      `${classes.cell} ${row.label === currentLabel ? classes.currentCell : ""}`;
    return [
      {
        name: "Score",
        width: "46%",
        themedPaddingRight: 0,
        value: (row) => <span className={`${cellClassName(row)} ${classes.scoreCell}`}>{row.scoreRange}</span>,
      },
      {
        name: "Label",
        width: "54%",
        themedPaddingRight: 0,
        value: (row) => <span className={cellClassName(row)}>{row.label}</span>,
      },
    ];
  }, [classes.cell, classes.currentCell, classes.scoreCell, currentLabel]);
  return (
    <InformativeModal
      open={open}
      onClose={onClose}
      dialogWidth="md"
      showDivider={false}
      customContentStyle={classes.content}
      PaperProps={{ className: classes.paper }}
      headerText={<span className={classes.title}>Flesch-Kincaid Reading Levels</span>}
      subHeaderText={
        <span className={classes.subtitle}>The score is excluding any text that is within parentheses</span>
      }
      additionalInfoElement={
        <>
          <div className={classes.table}>
            <CohereTable
              readonly
              hideFinalDivider
              tableColumns={tableColumns}
              data={ranges}
              renderHeader={(columns) => (
                <div className={classes.headerRow}>
                  {columns.map((column, index) => (
                    <div key={column.name} style={{ width: column.width, paddingLeft: index === 0 ? 28 : 0 }}>
                      {column.name}
                    </div>
                  ))}
                </div>
              )}
            />
          </div>
          <SecondaryButton className={classes.closeButton} onClick={onClose}>
            Close
          </SecondaryButton>
        </>
      }
    />
  );
}
